import { addDays, format, isSameDay, parseISO, startOfDay } from 'date-fns';
import i18n from 'i18next';
import type { MedicalAppointment, Medication, MedicationDose, Task } from '@/types/api';
import { msg, resolveMsg, type AnyTFunction, type Msg } from '@/i18n/msg';
import { autoTaskTitleMsg, isAllDayTask } from '@/lib/task-display';
import { formatTime } from '@/i18n/intl';
import { bcp47For } from '@/i18n/negotiate';
import type { ChipTone } from '@/components/editorial/StatusChip';
import { findDoseEvent, medicationSlotsOnDay, slotHour, toDateKey } from '@/lib/dose-display';


export type EventCategory = 'medical' | 'task' | 'medication' | 'stevens';

/**
 * Appointment type names — moved from `lib/medical-display.ts` (#2109 R3 L0)
 * and now a key map resolved at render (R3-B4). Only Calendar's two
 * appointment dialogs read it, so no English compatibility getter is kept.
 */
export const APPT_TYPE_LABEL_KEY = {
  chemotherapy:     'calendar:apptType.chemotherapy',
  radiation:        'calendar:apptType.radiation',
  scan:             'calendar:apptType.scan',
  lab:              'calendar:apptType.lab',
  oncology_consult: 'calendar:apptType.oncology_consult',
  surgery:          'calendar:apptType.surgery',
  physical_therapy: 'calendar:apptType.physical_therapy',
  social_worker:    'calendar:apptType.social_worker',
  other:            'calendar:apptType.other',
} as const satisfies Record<MedicalAppointment['appointment_type'], string>;

export const ALL_APPT_TYPES: readonly MedicalAppointment['appointment_type'][] = [
  'chemotherapy', 'radiation', 'scan', 'lab', 'oncology_consult', 'surgery', 'physical_therapy', 'social_worker', 'other',
];

/**
 * The event-class names. `stevens` has no key: that class is named by the
 * family's companion (`useCompanionName()`), which the views pass in.
 */
export const CATEGORY_LABEL_KEY = {
  medical:    'calendar:category.medical',
  task:       'calendar:category.task',
  medication: 'calendar:category.medication',
} as const satisfies Record<Exclude<EventCategory, 'stevens'>, string>;

/** The event class's name in the reader's language; the companion class is the companion's name. */
export function categoryLabel(category: EventCategory, companionName: string, t: AnyTFunction): string {
  if (category === 'stevens') return companionName;
  return resolveMsg(t, msg(CATEGORY_LABEL_KEY[category]));
}

/** "logged taken" — the dose record's status as a label, not the raw enum (R3-B22). */
export const DOSE_LOGGED_KEY = {
  taken:   'calendar:dose.logged.taken',
  skipped: 'calendar:dose.logged.skipped',
} as const satisfies Record<MedicationDose['status'], string>;

// Single source of truth for category colors across calendar.
/**
 * Per-source calendar colour (#724).
 *
 * `medical` used to render ROSE — so on a family's own calendar, every oncology
 * appointment was painted in the colour this platform uses for "overdue",
 * "broken", "escalated". An appointment is a KIND of event, not a problem, and
 * on a pediatric-cancer platform that distinction is not cosmetic. It is teal.
 *
 * `task` moves off indigo (indigo means interactive, never categorical) to
 * neutral — a task is the baseline event, and neutral is what "no claim" looks
 * like. `stevens` moves off emerald, which is the status-ok hue.
 */
export const CATEGORY_TOKENS: Record<
  EventCategory,
  { dot: string; rail: string; pill: string; weekBg: string; monthChip: string; iconColor: string; tone: ChipTone }
> = {
  medical: {
    dot:       'bg-cat-teal-fg',
    rail:      'bg-cat-teal-fg',
    pill:      'text-cat-teal-fg',
    weekBg:    'bg-cat-teal text-cat-teal-fg border-cat-teal-border',
    monthChip: 'bg-cat-teal text-cat-teal-fg',
    iconColor: 'text-cat-teal-fg',
    tone:      'teal',
  },
  task: {
    dot:       'bg-chip-neutral-fg',
    rail:      'bg-chip-neutral-fg',
    pill:      'text-chip-neutral-fg',
    weekBg:    'bg-chip-neutral text-chip-neutral-fg border-chip-neutral-border',
    monthChip: 'bg-chip-neutral text-chip-neutral-fg',
    iconColor: 'text-chip-neutral-fg',
    tone:      'neutral',
  },
  medication: {
    dot:       'bg-cat-violet-fg',
    rail:      'bg-cat-violet-fg',
    pill:      'text-cat-violet-fg',
    weekBg:    'bg-cat-violet text-cat-violet-fg border-cat-violet-border',
    monthChip: 'bg-cat-violet text-cat-violet-fg',
    iconColor: 'text-cat-violet-fg',
    tone:      'violet',
  },
  stevens: {
    dot:       'bg-cat-magenta-fg',
    rail:      'bg-cat-magenta-fg',
    pill:      'text-cat-magenta-fg',
    weekBg:    'bg-cat-magenta text-cat-magenta-fg border-cat-magenta-border italic',
    monthChip: 'bg-cat-magenta text-cat-magenta-fg italic',
    iconColor: 'text-cat-magenta-fg',
    tone:      'magenta',
  },
};

export type CalendarSource = 'task' | 'appointment' | 'medication' | 'stevens-suggestion';

export interface UnifiedEvent {
  id: string;
  source: CalendarSource;
  category: EventCategory;
  /** The stored title (family data), or the English of `titleMsg`. */
  title: string;
  /**
   * Set when the title is platform copy rather than family data — a derived
   * companion suggestion, an auto-staged task's template (#2109 R3-B3/B14).
   * `eventTitle` resolves it in the reader's language.
   */
  titleMsg?: Msg;
  who?: string;
  /** The `who` line as descriptors, joined with " · " at render (platform copy beside family data). */
  whoMsgs?: Msg[];
  note?: string;
  start: Date;
  end: Date;
  /**
   * A date-only task (#2410, D-2410): `start` is the local start of its day
   * and `end` the next day's start. The views say "All day" instead of a
   * clock and put it first in its day.
   */
  allDay?: boolean;
  urgent?: boolean;
  claimedByName?: string;
  // Used when navigating to detail / completion pages.
  taskId?: string;
  appointmentId?: string;
  medicationId?: string;
  /**
   * The appointment's status (#2433). A cancelled visit stays on the calendar,
   * because the parent can restore it, but the views mark it as cancelled.
   */
  status?: MedicalAppointment['status'];
}

/** True for an appointment the family cancelled (#2433). */
export function isCancelledEvent(e: UnifiedEvent): boolean {
  return e.status === 'cancelled';
}

// ── Source → unified shape ────────────────────────────────────────────────

const TASK_CATEGORY_TO_EVENT: Record<Task['category'], EventCategory> = {
  hospital_stay: 'medical',
  meal:          'task',
  ride:          'task',
  childcare:     'task',
  errand:        'task',
  other:         'task',
};

export function taskToEvent(task: Task, helperName?: string): UnifiedEvent | null {
  if (!task.date_time) return null;
  if (task.status === 'cancelled') return null;
  const allDay = isAllDayTask(task);
  // An all-day task spans its local day; the stored instant is that day's start.
  const start = allDay ? startOfDay(parseISO(task.date_time)) : parseISO(task.date_time);
  const end = allDay
    ? addDays(start, 1)
    : new Date(start.getTime() + (task.duration_minutes ?? 60) * 60_000);
  const urgent =
    task.status === 'open' && (task.urgency === 'critical' || task.urgency === 'high');
  return {
    id: `task-${task._id}`,
    source: 'task',
    category: TASK_CATEGORY_TO_EVENT[task.category] ?? 'task',
    title: task.title,
    ...(autoTaskTitleMsg(task) ? { titleMsg: autoTaskTitleMsg(task)! } : {}),
    // No helpers exist on the platform anymore (P2b); an open task is simply
    // the family's own, so it carries no `who` line (2026-09-10 declutter).
    who: helperName || undefined,
    note: task.description ?? undefined,
    start,
    end,
    ...(allDay ? { allDay: true } : {}),
    urgent,
    claimedByName: helperName,
    taskId: task._id,
  };
}

export function appointmentToEvent(a: MedicalAppointment): UnifiedEvent {
  const start = parseISO(a.date_time);
  const end = new Date(start.getTime() + (a.duration_mins ?? 60) * 60_000);
  return {
    id: `appt-${a._id}`,
    source: 'appointment',
    category: 'medical',
    title: a.title,
    who: [a.provider, a.location].filter(Boolean).join(' · ') || undefined,
    note: a.notes ?? undefined,
    start,
    end,
    appointmentId: a._id,
    status: a.status,
  };
}

// Synthesize a daily dose event for each active medication × time_of_day slot
// across the visible date range. Fanout (active flag, start/end window, slot
// defaulting, slot hours) is delegated to lib/dose-display.ts so the calendar
// and the dashboard cannot drift apart (#846).
//
// These are NEUTRAL schedule entries — a calendar event says "a dose is
// scheduled here", never "it was taken". When the caller passes the family's
// logged dose events, a slot WITH a record may reflect that logged status in
// its subtitle; the absence of a record stays a plain schedule entry, carrying
// no adherence claim in either direction.
export function medicationDoseEvents(
  meds: Medication[],
  rangeStart: Date,
  rangeEnd: Date,
  doses?: MedicationDose[],
): UnifiedEvent[] {
  const out: UnifiedEvent[] = [];
  const startDay = startOfDay(rangeStart);
  const endDay = startOfDay(rangeEnd);
  for (const m of meds) {
    let cursor = startDay;
    while (cursor <= endDay) {
      const dateKey = toDateKey(cursor);
      for (const slot of medicationSlotsOnDay(m, cursor)) {
        const start = new Date(cursor);
        start.setHours(slotHour(slot), 0, 0, 0);
        const end = new Date(start.getTime() + 30 * 60_000);
        const logged = findDoseEvent(doses, m._id, dateKey, slot);
        // The dose is data; "with food" and "logged <status>" are copy.
        const doseMsg = m.dose
          ? msg(m.with_food ? 'calendar:dose.withFood' : 'calendar:dose.plain', { dose: m.dose })
          : null;
        const loggedKey = logged ? DOSE_LOGGED_KEY[logged.status] : undefined;
        const whoMsgs = [doseMsg, loggedKey ? msg(loggedKey) : null]
          .filter((x): x is Msg => x !== null);
        const en = i18n.getFixedT('en');
        out.push({
          id: `med-${m._id}-${dateKey}-${slot}`,
          source: 'medication',
          category: 'medication',
          title: m.medication_name,
          who: whoMsgs.length ? whoMsgs.map((w) => resolveMsg(en, w)).join(' · ') : undefined,
          ...(whoMsgs.length ? { whoMsgs } : {}),
          start,
          end,
          medicationId: m._id,
        });
      }
      cursor = addDays(cursor, 1);
    }
  }
  return out;
}

// Day-after-chemo "Likely low-energy window" — Stevens-suggested ephemeral.
// Only a visit that is booked or happened earns it (#2433): the day after a
// cancelled or missed chemo is not a low-energy day.
const NO_SUGGESTION_STATUSES = new Set<MedicalAppointment['status']>(['cancelled', 'missed']);

/**
 * #2433 integrator ruling: a visit counts for the calendar's suggestions
 * unless it was cancelled or missed. The grid's rest-day suggestion and the
 * calendar companion remark (CalendarStevensCallout) share this one rule, so
 * an attended chemo still earns its next-day suggestion and remark.
 */
export function countsForCalendarSuggestion(a: Pick<MedicalAppointment, 'status'>): boolean {
  return !NO_SUGGESTION_STATUSES.has(a.status);
}

const LOW_ENERGY_TYPES = new Set<MedicalAppointment['appointment_type']>([
  'chemotherapy',
  'radiation',
  'surgery',
]);

export function stevensSuggestedEvents(appts: MedicalAppointment[]): UnifiedEvent[] {
  const out: UnifiedEvent[] = [];
  const seen = new Set<string>();
  for (const a of appts) {
    if (!LOW_ENERGY_TYPES.has(a.appointment_type)) continue;
    if (!countsForCalendarSuggestion(a)) continue;
    const day = addDays(parseISO(a.date_time), 1);
    const key = day.toISOString().slice(0, 10);
    if (seen.has(key)) continue;
    seen.add(key);
    const start = new Date(day);
    start.setHours(9, 0, 0, 0);
    const end = new Date(day);
    end.setHours(13, 0, 0, 0);
    const titleMsg = msg('calendar:suggested.lowEnergy');
    const whoMsg = msg('calendar:suggested.dayAfter', { title: a.title });
    const en = i18n.getFixedT('en');
    out.push({
      id: `stevens-${key}`,
      source: 'stevens-suggestion',
      category: 'stevens',
      title: resolveMsg(en, titleMsg),
      titleMsg,
      who: resolveMsg(en, whoMsg),
      whoMsgs: [whoMsg],
      start,
      end,
    });
  }
  return out;
}

/** The title the reader sees: the descriptor when the title is platform copy, else the stored title. */
export function eventTitle(e: UnifiedEvent, t: AnyTFunction): string {
  return e.titleMsg ? resolveMsg(t, e.titleMsg) : e.title;
}

/** The `who` line the reader sees. */
export function eventWho(e: UnifiedEvent, t: AnyTFunction): string | undefined {
  if (e.whoMsgs?.length) return e.whoMsgs.map((w) => resolveMsg(t, w)).join(' · ');
  return e.who;
}

// ── Search ────────────────────────────────────────────────────────────────

/**
 * The toolbar search (declutter 2026-09-10, spec §5): client-side over the
 * loaded events, title only, case-insensitive. An empty query matches every
 * event, so closing the field — which clears the query — never leaves the
 * calendar filtered by something the parent can no longer see.
 */
export function eventMatchesSearch(e: UnifiedEvent, q: string, t: AnyTFunction = i18n.t): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  // The title the reader sees (#2109 R3-B4): a derived suggestion is found by its translated name.
  return eventTitle(e, t).toLowerCase().includes(needle);
}

// ── Range / grouping helpers ──────────────────────────────────────────────

/**
 * Order within one day (#2410): all-day events lead, then by start time.
 * Every view and grouping helper sorts a day's events with this one rule.
 */
export function compareEvents(a: UnifiedEvent, b: UnifiedEvent): number {
  if (!!a.allDay !== !!b.allDay) return a.allDay ? -1 : 1;
  return a.start.getTime() - b.start.getTime();
}

export function eventsByDay(events: UnifiedEvent[]): Map<string, UnifiedEvent[]> {
  const m = new Map<string, UnifiedEvent[]>();
  for (const e of events) {
    const key = format(e.start, 'yyyy-MM-dd');
    const arr = m.get(key) ?? [];
    arr.push(e);
    m.set(key, arr);
  }
  for (const arr of m.values()) {
    arr.sort(compareEvents);
  }
  return m;
}

export function eventsOnDay(events: UnifiedEvent[], day: Date): UnifiedEvent[] {
  return events
    .filter((e) => isSameDay(e.start, day))
    .sort(compareEvents);
}

// ── View persistence ──────────────────────────────────────────────────────

export type CalendarView = 'agenda' | 'week' | 'month';

const VIEW_KEY = 'trf:calendar-view';

export function loadCalendarView(fallback: CalendarView = 'agenda'): CalendarView {
  if (typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(VIEW_KEY);
  if (raw === 'agenda' || raw === 'week' || raw === 'month') return raw;
  return fallback;
}

export function persistCalendarView(v: CalendarView): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(VIEW_KEY, v);
}

// Hidden-categories persistence
const HIDDEN_KEY = 'trf:calendar-hidden-cats';

export function loadHiddenCats(): Set<EventCategory> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(HIDDEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as EventCategory[];
    return new Set(parsed.filter((c): c is EventCategory => ['medical', 'task', 'medication', 'stevens'].includes(c)));
  } catch {
    return new Set();
  }
}

export function persistHiddenCats(cats: Set<EventCategory>): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(HIDDEN_KEY, JSON.stringify([...cats]));
}

// ── Time formatting (matches design's mono short-times) ───────────────────
//
// Through Intl (#2109 R3-B5): the locale decides the clock. A 12-hour locale
// keeps the design's compact mono form — the hour, the minutes only when
// they are not :00, and the day period with no space ("9AM", "2:30PM") — so
// the English grid is unchanged. A 24-hour locale (es, pt, fr, zh) reads its
// own clock ("18:00", "9:30").

function clockParts(d: Date, locale?: string, withMinutes = true): Intl.DateTimeFormatPart[] {
  const tag = locale ?? i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const options: Intl.DateTimeFormatOptions = withMinutes ? { hour: 'numeric', minute: '2-digit' } : { hour: 'numeric' };
  return new Intl.DateTimeFormat(bcp47For(tag), options).formatToParts(d);
}

function is12Hour(parts: Intl.DateTimeFormatPart[]): boolean {
  return parts.some((p) => p.type === 'dayPeriod');
}

export function fmtTimeShort(d: Date, locale?: string): string {
  const parts = clockParts(d, locale);
  if (!is12Hour(parts)) return formatTime(d, { locale });
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const period = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').replace(/[\s.]/g, '').toUpperCase();
  return minute === '00' ? `${hour}${period}` : `${hour}:${minute}${period}`;
}

/**
 * The week grid's hour gutter: "7A" · "12P" · "1P" in a 12-hour locale (the
 * design's one-letter day period), the locale's own "07:00" / "7:00"
 * otherwise. Replaces the hand-built `${h}A` / `${h - 12}P` gutter.
 */
export function fmtHourGutter(hour: number, locale?: string): string {
  const d = new Date(2000, 0, 3, hour, 0, 0, 0);
  const parts = clockParts(d, locale, false);
  if (!is12Hour(parts)) return formatTime(d, { locale });
  const h = parts.find((p) => p.type === 'hour')?.value ?? String(hour);
  const period = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').replace(/[\s.]/g, '');
  return `${h}${period.charAt(0).toUpperCase()}`;
}
